import React, { useMemo, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Minus, Plus, Check, ArrowUpRight, ShoppingBag, Trash2 } from 'lucide-react';
import { AlmondIcon } from '../components/AlmondIcon';
import PickupConfirmModal from '../components/PickupConfirmModal';
import useReveal from '../hooks/useReveal';
import { products as baseProducts, images } from '../mock/mock';
import { useCart } from '../context/CartContext';
import { resolveImg, useContent } from '../context/ContentContext';

function PageHead() {
  return (
    <section className="relative pt-44 pb-16 min-h-[480px] flex items-center bg-[#E9E7E2] text-[#2C3A28] overflow-hidden">
      <img
        src={images.productsLineup}
        alt="The Al Naturale nut milk range"
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#E9E7E2] via-[#E9E7E2]/70 to-[#E9E7E2]/40" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <span className="text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
          Our products
        </span>
        <h1 className="mt-4 text-6xl sm:text-7xl max-w-2xl leading-[1.05]" style={{ fontFamily: "'Pinyon Script', cursive" }}>
          Nut milks, the way nature intended
        </h1>
        <p className="mt-5 max-w-xl font-display text-[#3B2A20]/80 text-lg sm:text-xl leading-relaxed">
          Every bottle: activated nuts, filtered water, a single date, a touch of salt.
          Nothing else. Order online for delivery, or build a box to pick up in store.
        </p>
      </div>
    </section>
  );
}

export default function Products() {
  useReveal([]);
  const { items, add, clear, count } = useCart();
  const location = useLocation();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { content, txt } = useContent();
  const products = useMemo(() => {
    const ov = content.products;
    if (!Array.isArray(ov) || !ov.length) return baseProducts;
    return baseProducts.map((p) => {
      const o = ov.find((x) => x.id === p.id);
      if (!o) return p;
      return {
        ...p,
        tagline: o.tagline ?? p.tagline,
        description: o.description ?? p.description,
        tastingNotes: o.tastingNotes ?? p.tastingNotes,
        notes: o.notes && o.notes.length ? o.notes : p.notes,
        sizes: o.sizes && o.sizes.length ? o.sizes : p.sizes,
        image: o.image || p.image,
      };
    });
  }, [content.products]);

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    const el = document.getElementById(id);
    if (!el) return;
    const scrollToEl = () => {
      const navH = document.querySelector('header')?.offsetHeight || 150;
      const y = el.getBoundingClientRect().top + window.scrollY - navH - 28;
      window.scrollTo({ top: id === 'pickup-box' ? el.getBoundingClientRect().top + window.scrollY - navH : y, behavior: 'smooth' });
    };
    const t1 = setTimeout(scrollToEl, 350);
    const t2 = setTimeout(scrollToEl, 1100);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [location]);

  const parseKey = (key) => {
    const [id, si] = key.split('__');
    const p = products.find((x) => x.id === id);
    const size = p && p.sizes ? p.sizes[Number(si)] : null;
    return { p, size };
  };

  const total = useMemo(
    () =>
      Object.entries(items).reduce((sum, [key, qty]) => {
        const { size } = parseKey(key);
        return sum + (size ? size.price * qty : 0);
      }, 0),
    [items]
  );

  const lines = useMemo(
    () =>
      Object.entries(items)
        .map(([key, qty]) => {
          const { p, size } = parseKey(key);
          if (!p || !size) return null;
          return { key, qty, name: p.name, badge: p.badge, size: size.label, amount: size.price * qty };
        })
        .filter(Boolean),
    [items]
  );

  const scrollToBox = () => {
    const el = document.getElementById('pickup-box');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="bg-[#E9EBED]">
      <div className="pt-28" />
      {/* banner hidden, restore <PageHead /> to reinstate */}

      {/* product detail cards */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 space-y-8">
          {products.map((p, i) => (
            <React.Fragment key={p.id}>
            <div
              id={p.id}
              className={`reveal grid lg:grid-cols-2 gap-8 items-center rounded-[2rem] overflow-hidden bg-[#E9EBED] scroll-mt-[150px] ${
                i % 2 === 1 ? 'lg:[&>*:first-child]:order-2' : ''
              }`}
            >
              <div className="relative flex items-center justify-center py-12 min-h-[580px]">
                <span
                  className="absolute top-6 left-6 rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-wide text-[#E9EBED]"
                  style={{ backgroundColor: p.accent }}
                >
                  {p.badge}
                </span>
                <img
                  src={resolveImg(p.image)}
                  alt={`${p.name} ${p.badge}`}
                  className="h-[540px] object-contain drop-shadow-[0_25px_30px_rgba(0,0,0,0.2)]"
                />
              </div>
              <div className="px-8 sm:px-12 py-10">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-[#2C3A28]">
                  {p.tagline}
                </p>
                <h2 className="mt-2 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28]">
                  {p.name} {p.badge.split(' ')[0]}
                </h2>
                <p className="mt-4 text-[#2C3A28] text-lg leading-relaxed">{p.description}</p>
                {p.tastingNotes && (
                  <div className="mt-5">
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#2C3A28]">
                      Tasting notes
                    </p>
                    <p className="mt-1 font-display font-bold text-[#2C3A28] text-lg leading-snug">
                      {p.tastingNotes}
                    </p>
                  </div>
                )}
                <ul className="mt-6 grid grid-cols-2 gap-3">
                  {p.notes.map((n) => (
                    <li key={n} className="flex items-center gap-2 text-[#2C3A28]">
                      <span
                        className="w-5 h-5 rounded-full flex items-center justify-center text-[#E9EBED]"
                        style={{ backgroundColor: p.accent }}
                      >
                        <Check size={12} />
                      </span>
                      <span className="text-sm">{n}</span>
                    </li>
                  ))}
                </ul>

                {/* size rows with in-store pickup add control */}
                <div className="mt-8 space-y-2">
                  {p.sizes.map((s, si) => {
                    const key = `${p.id}__${si}`;
                    return (
                      <div
                        key={s.label}
                        className="flex items-center justify-between gap-4 rounded-2xl bg-[#DCDFE2] px-5 py-3"
                        data-testid={`product-size-${p.id}-${s.label}`}
                      >
                        <div className="flex items-baseline gap-3">
                          <span className="font-display text-xl text-[#2C3A28]">{s.label}</span>
                          <span className="text-[#2C3A28] font-semibold">${s.price.toFixed(2)}</span>
                        </div>
                        {items[key] ? (
                          <div className="flex items-center gap-2 rounded-full p-1 bg-[#2C3A28]">
                            <button
                              onClick={() => add(key, -1)}
                              className="w-8 h-8 rounded-full bg-[#E9EBED]/20 text-[#E9EBED] flex items-center justify-center hover:bg-[#E9EBED]/30"
                              data-testid={`pickup-minus-${p.id}-${s.label}`}
                            >
                              <Minus size={15} />
                            </button>
                            <span className="text-[#E9EBED] font-semibold w-6 text-center">{items[key]}</span>
                            <button
                              onClick={() => add(key, 1)}
                              className="w-8 h-8 rounded-full bg-[#E9EBED]/20 text-[#E9EBED] flex items-center justify-center hover:bg-[#E9EBED]/30"
                              data-testid={`pickup-plus-${p.id}-${s.label}`}
                            >
                              <Plus size={15} />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => add(key, 1)}
                            className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 bg-[#E9EBED] text-[#2C3A28] border border-[#2C3A28]/30 text-sm font-semibold transition-transform hover:-translate-y-0.5"
                            data-testid={`pickup-add-${p.id}-${s.label}`}
                          >
                            <Plus size={15} /> In Store Pick Up
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>

                <a
                  href={p.uberUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`product-order-${p.id}`}
                  className="mt-6 inline-flex items-center gap-2 rounded-full px-7 py-3.5 text-[#2C3A28] text-sm font-semibold transition-transform hover:-translate-y-0.5 bg-[#E9E5D8]"
                >
                  Order Online
                  <ArrowUpRight size={16} />
                </a>
              </div>
            </div>
            {i < products.length - 1 && (
              <div className="max-w-5xl mx-auto w-full h-px bg-[#2C3A28]/15" />
            )}
            </React.Fragment>
          ))}
          <div className="max-w-5xl mx-auto w-full h-px bg-[#2C3A28]/15" />
        </div>
      </section>

      {/* IN-STORE PICK UP box */}
      <section id="pickup-box" className="py-20 bg-[#E9EBED] scroll-mt-24">
        <div className="max-w-3xl mx-auto px-5 sm:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <span className="inline-flex items-center gap-2 text-[#2C3A28] text-sm font-semibold uppercase tracking-[0.2em]">
              <AlmondIcon size={15} className="text-[#D2B489]" /> In store pick up
            </span>
            <h2 className="mt-4 font-display text-4xl sm:text-5xl font-semibold uppercase text-[#2C3A28]">
              {txt('products', 'pickupHeading', 'Your pick-up box')}
            </h2>
            <p className="mt-4 text-[#3B2A20]/70 text-lg">
              {txt('products', 'pickupBody', "Add your bottles above and we'll have them ready to collect from the St Kilda cafe.")}
            </p>
          </div>

          <div className="reveal mt-10 rounded-3xl bg-[#2C3A28] text-[#EDEAE0] p-8 flex flex-col">
            <div className="flex items-center gap-2">
              <ShoppingBag size={20} className="text-[#2C3A28]" />
              <h3 className="font-display text-2xl text-[#E9E5D8]">Pick-up box</h3>
            </div>
            <div className="mt-6 flex-1 space-y-3 min-h-[60px]">
              {count === 0 && (
                <p className="text-[#EDEAE0]/50 text-sm" data-testid="pickup-empty-msg">
                  Your box is empty. Add some milks above to pick up in store.
                </p>
              )}
              {Object.entries(items).map(([key, qty]) => {
                const { p, size } = parseKey(key);
                if (!p || !size) return null;
                return (
                  <div key={key} className="flex items-center justify-between text-sm">
                    <span className="text-[#EDEAE0]/85">
                      {qty} × {p.name}{' '}
                      <span className="text-[#EDEAE0]/50">({p.badge} · {size.label})</span>
                    </span>
                    <span className="font-semibold text-[#E9EBED]">${(size.price * qty).toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 pt-5 border-t border-[#EDEAE0]/15">
              <div className="flex items-center justify-between">
                <span className="text-[#EDEAE0]/70">Total ({count} bottles)</span>
                <span className="font-display text-3xl text-[#E9EBED]" data-testid="pickup-total">${total.toFixed(2)}</span>
              </div>
              <button
                disabled={count === 0}
                onClick={() => setConfirmOpen(true)}
                data-testid="confirm-pickup-order-btn"
                className="mt-5 w-full rounded-full bg-[#2C3A28] text-white py-4 font-semibold hover:bg-[#1f2a1c] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Confirm order
              </button>
              {count > 0 && (
                <button
                  onClick={clear}
                  data-testid="pickup-clear-btn"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm text-[#EDEAE0]/60 hover:text-[#EDEAE0]"
                >
                  <Trash2 size={15} /> Clear box
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      <PickupConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        lines={lines}
        total={total}
        count={count}
        onDone={clear}
      />
    </div>
  );
}
